import type { VNode, InjectionKey } from "vue";
import { unref, Comment, inject } from "vue";

export function clamp(num: number, min: number, max: number): number {
  return Math.min(Math.max(num, min), max);
}

export function omitUnknowns(
  obj: Record<string, unknown>
): Record<string, unknown> {
  Object.keys(obj)
    .filter(k => obj[k] === null || obj[k] === undefined)
    .forEach(k => delete obj[k]);
  return obj;
}

export function mapKeys<T>(
  obj: T[],
  mapper: (value: T, key: string) => string
): Record<string, T> {
  return Object.entries(obj).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [mapper(value, key)]: value
    }),
    {}
  );
}

export function omit<T>(
  obj: T,
  cb: (value: T[keyof T], key: keyof T) => boolean
): Partial<T> {
  if (obj) {
    return Object.keys(obj).reduce<Partial<T>>((acc, b) => {
      const key = b as keyof T;
      if (!cb(obj[key], key)) acc[key] = obj[key];
      return acc;
    }, {});
  }
  return {};
}

export function isEmptyObject(obj?: Record<string, unknown> | null): boolean {
  return (
    !!obj &&
    Object.keys(obj).length === 0 &&
    Object.getPrototypeOf(obj) === Object.prototype
  );
}

export const isDate = (d: unknown): boolean => d instanceof Date;
export const isEmpty = (o: Record<string, unknown>): boolean =>
  Object.keys(o).length === 0;
export const isObject = (o: unknown): boolean =>
  o != null && typeof o === "object";
export const properObject = (
  o: Record<string, unknown>
): Record<string, unknown> => (isObject(o) && !o.hasOwnProperty ? { ...o } : o);

export function humanize(str: string): string {
  let i;
  const frags = str.split("_");
  console.log(str);
  console.log(frags);
  for (i = 0; i < frags.length; i++) {
    frags[i] = frags[i].charAt(0).toUpperCase() + frags[i].slice(1);
  }
  const humanized = frags.join(" ");
  return humanized.startsWith("Oil Volume Factor")
    ? humanized.replace("Oil Volume Factor", "Oil FVF")
    : humanized;
}

export function isVNodeEmpty(
  vnodes: VNode | VNode[] | undefined | null
): boolean {
  return (
    !!vnodes &&
    (Array.isArray(vnodes)
      ? vnodes.every(vnode => vnode.type !== Comment)
      : vnodes.type !== Comment)
  );
}

export function coalesce(...args: unknown[]): unknown {
  if (!args || !args.length) {
    return undefined;
  }
  let temp = args[0];
  temp = typeof temp === "function" ? temp() : temp;
  return temp || coalesce(args.slice(1));
}

export function injectStrict<T>(
  key: InjectionKey<T> | string,
  fallback?: T
): T {
  const resolved = inject(key, fallback);
  if (!resolved) {
    const keyText = typeof key === "string" ? key : key.description;
    throw new Error(`Could not resolve ${keyText}`);
  }
  return resolved;
}

export function decimalFormatter(
  num: number | null,
  withThousandsSeparator = true,
  maxDecimals = 2,
  minDecimals = 0,
  localOverride: string | string[] | undefined = undefined
): string {
  return new Intl.NumberFormat(localOverride, {
    maximumFractionDigits: maxDecimals,
    minimumFractionDigits: minDecimals,
    useGrouping: withThousandsSeparator
  }).format(num ?? 0);
}

export function roundFloat(num: number): number {
  return Number(num.toPrecision(12));
}

export const DEBOUNCE_TIME_MS = 500;

export function deepUnref(
  refObject: Record<string, unknown>
): Record<string, unknown> {
  const unrefVal = unref(refObject);

  if (isObject(unrefVal)) {
    return Object.entries(unrefVal).reduce<Record<string, unknown>>(
      (obj, [key, val]) => {
        return Object.assign(obj, {
          [key]: deepUnref(val as Record<string, unknown>)
        });
      },
      {}
    );
  } else {
    return unrefVal;
  }
}

export function arrayOfInts(numValues: number, start = 0): number[] {
  return new Array(numValues).fill(null).map((_, i) => i + start);
}

export function asyncTimeout(ts: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(() => {
      return resolve();
    }, ts);
  });
}

export function removeArrayElement<
  T extends Record<string, unknown> | number | string | object
>(array: Array<T>, element: T, identifier?: string): boolean {
  const idx = identifier
    ? array.findIndex(
        item =>
          typeof item === "object" &&
          item !== null &&
          identifier in item &&
          typeof element === "object" &&
          element !== null &&
          identifier in element &&
          (item as Record<string, unknown>)[identifier] ===
            (element as Record<string, unknown>)[identifier]
      )
    : array.indexOf(element);

  if (idx < 0) return false;

  array.splice(idx, 1);
  return true;
}

export const toggleObservable = (
  resizeObserver: ResizeObserver,
  currentDomEl: Element | undefined,
  previousDomEl: Element | undefined
) => {
  if (currentDomEl === previousDomEl) return;
  if (currentDomEl && !previousDomEl) {
    resizeObserver.observe(currentDomEl);
  } else if (!currentDomEl && previousDomEl) {
    resizeObserver.unobserve(previousDomEl);
  } else if (currentDomEl && previousDomEl) {
    resizeObserver.unobserve(previousDomEl);
    resizeObserver.observe(currentDomEl);
  }
};
export const isNullOrUndefined = (value: unknown) => {
  return value === null || value === undefined;
};

export const isNullOrUndefinedOrEmptyString = (value: unknown) => {
  if (isNullOrUndefined(value)) return true;
  return typeof value === "string" && value.trim() === "";
};
export function formatNumber(val: number, decimalPlaces: number = 2) {
  return Math.round(val * 10 ** decimalPlaces) / 10 ** decimalPlaces;
}

export function get<T>(
  obj: { [key: string]: unknown } | null | undefined,
  path: string,
  defaultValue?: T
): T | undefined {
  if (!obj || typeof path !== "string") return defaultValue;

  const splitPath = path.split(".");
  let current: { [key: string]: unknown } | undefined = obj;

  for (let i = 0; i < splitPath.length; i++) {
    if (current === undefined) return defaultValue;
    if (typeof current !== "object" || current === null) {
      return defaultValue;
    }
    current = current[splitPath[i]] as { [key: string]: unknown } | undefined;
  }

  return current === undefined ? defaultValue : (current as T);
}

export const safeNumber = (
  n: number | null | undefined,
  fallback = 0
): number => (Number.isFinite(n as number) ? (n as number) : fallback);
